/**
 * The Summary sheet of the Bill Runs XLSX export: the metric counts the page
 * shows (lib/billRunMetrics), one line per card, plus the dollars behind the
 * Overcharge and Undercharge cards.
 *
 * The dollars are counted over the same rows as those cards (every condition
 * except Status and Status details), as the sum of each bill's |variance|.
 */
import type { BillRunRow } from './billRunRows';
import type { BillRunMetrics } from './billRunMetrics';
import { matchesBillRunConditions, type BillRunConditions, type MatchOptions } from './billRunConditions';
import { billRunStatus } from './billRunStatus';
import { SEND_STATUS_LABELS } from './reportStatus';
import { SEND_STATUS_ORDER } from './billFilters';
import { WORKFLOW_LABELS, WORKFLOW_ORDER } from './billWorkflow';

export type OutcomeDollars = { overcharge: number; undercharge: number };

export type SummaryLine = { Section: string; Metric: string; Value: string | number };

const cents = (n: number) => Math.round(n * 100) / 100;

/** $ over and under, across the bills the Overcharge / Undercharge cards count. */
export function outcomeDollars(
  rows: readonly BillRunRow[],
  conditions: BillRunConditions,
  opts: Pick<MatchOptions, 'runBillIds' | 'driverBillIds'> = {},
): OutcomeDollars {
  const out: OutcomeDollars = { overcharge: 0, undercharge: 0 };
  for (const r of rows) {
    if (r.kind !== 'bill' || !r.inScope || r.figures?.variance == null) continue;
    if (!matchesBillRunConditions(r, conditions, { ...opts, skip: ['status', 'details'] })) continue;
    const d = billRunStatus(r).details;
    if (d === 'overcharge' || d === 'undercharge') out[d] += Math.abs(r.figures.variance);
  }
  return { overcharge: cents(out.overcharge), undercharge: cents(out.undercharge) };
}

export type BillRunsSummaryInput = {
  /** The period shown, e.g. "Aug 2026 – Sep 2026". */
  period: string;
  /** The tab exported, e.g. "Bills". */
  tab: string;
  /** Rows on the exported sheet. */
  rowCount: number;
  metrics: BillRunMetrics;
  dollars: OutcomeDollars;
};

/** Summary sheet rows: Section · Metric · Value. */
export function billRunsSummaryRows({ period, tab, rowCount, metrics, dollars }: BillRunsSummaryInput): SummaryLine[] {
  const line = (Section: string, Metric: string, Value: string | number): SummaryLine => ({ Section, Metric, Value });
  const cov = metrics.coverage.total;
  const o = metrics.outcome;
  return [
    line('Report', 'Period', period),
    line('Report', 'Tab', tab),
    line('Report', 'Rows exported', rowCount),
    line('Coverage', 'Expected', cov.expected),
    line('Coverage', 'Has bill', cov.hasBill),
    line('Coverage', 'Missing', cov.missing),
    line('Coverage', 'Never billed', metrics.coverage.neverBilled),
    line('Coverage', 'Covered', cov.covered),
    line('Coverage', 'Not expected', cov.notExpected),
    line('Outcome', 'Matched', o.matched),
    line('Outcome', 'Overcharge', o.overcharge),
    line('Outcome', 'Overcharge $', dollars.overcharge),
    line('Outcome', 'Undercharge', o.undercharge),
    line('Outcome', 'Undercharge $', dollars.undercharge),
    line('Outcome', 'Mismatch not yet classified', o.mismatchPending),
    line('Outcome', 'No bill yet', o.noBill),
    line('Outcome', 'Awaiting validation', o.awaitingValidation),
    ...SEND_STATUS_ORDER.map((s) => line('Send status', SEND_STATUS_LABELS[s], metrics.send[s] ?? 0)),
    ...WORKFLOW_ORDER.map((s) => line('Progress', WORKFLOW_LABELS[s], metrics.progress[s] ?? 0)),
  ];
}
